import {
  Button,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from '@ais-ui/ui-core';
import React, { useState } from 'react';
import { PiDownloadSimpleThin } from 'react-icons/pi';
import { getBasePath, useEventdetectorApi } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';

interface EventButtonProps {
  id: string;
  isLoading: boolean;
  Icon: React.ElementType;
  onClick: () => void;
  text: string;
}

const EventButton = ({
  id,
  isLoading,
  Icon,
  onClick,
  text,
}: EventButtonProps) => {
  return (
    <Button
      id={id}
      variant="ghost"
      onClick={onClick}
      disabled={isLoading}
      className="flex gap-x-2 col-span-2 justify-start"
    >
      {!isLoading ? (
        <Icon></Icon>
      ) : (
        <Icons.spinner className="h-4 w-4 animate-spin" />
      )}
      {text}
    </Button>
  );
};

interface EventActionsMenuProps {
  start: Date | undefined;
  end: Date | undefined;
}

export const EventActionsMenu = ({ start, end }: EventActionsMenuProps) => {
  const { messages } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [isPreparingDownload, setIsPreparingDownload] =
    useState<boolean>(false);
  const eventDetectorApi = useEventdetectorApi();
  const { toast } = useToast();

  const onDownload = async () => {
    if (start && end) {
      setIsPreparingDownload(true);

      const { url: downloadPath } = await eventDetectorApi.exportGet({
        start: start.getTime().toString(),
        end: end.getTime().toString(),
        serveruid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
      });

      const baseUrl = getBasePath();
      const fileUrl = baseUrl + 'aisweb/' + downloadPath;

      // Create an anchor element and trigger the download
      const anchor = document.createElement('a');
      anchor.href = fileUrl;

      anchor.download = `events_${start.toISOString()}_${end.toISOString()}.csv`;

      anchor.click();

      setIsPreparingDownload(false);

      toast({
        className: 'export-events-success-toast',
        title: messages.genericToastTitleSuccess,
        description: messages.eventsActionsMenuExportSuccessMessage,
      });
    } else {
      toast({
        className: 'ship-error-toast',
        title: messages.eventsActionsMenuExportErrorTitle,
        description: messages.eventsActionsMenuExportErrorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex w-full justify-end">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button>{messages.eventsActionsMenuButtonText}</Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" side="left" align="end">
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">
                {messages.eventsActionsMenuTitle}
              </h4>
              <p className="text-sm text-muted-foreground">
                {messages.eventsActionsMenuSubtitle}
              </p>
            </div>
            <div className="grid gap-2"></div>
          </div>
          <div className="grid grid-cols-4">
            <EventButton
              id="download-events-button"
              isLoading={isPreparingDownload}
              Icon={PiDownloadSimpleThin}
              onClick={() => onDownload()}
              text={messages.eventsActionsMenuDownloandText}
            ></EventButton>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
